"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
function InquiryFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-12 text-center bg-card border border-border rounded-lg shadow-md">
        <h3 className="font-default text-2xl text-primary mb-4">پیام شما ارسال شد</h3>
        <p className="text-foreground mb-8">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="tracking-wide rounded-lg">
          ارسال پیام جدید
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "نام شما الزامی است"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">نام و نام خانوادگی</FormLabel>
              <FormControl>
                <Input placeholder="مثلاً: مریم احمدی" className="rounded-lg bg-background border-input focus:ring-secondary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "آدرس ایمیل الزامی است",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "لطفاً یک ایمیل معتبر وارد کنید"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">آدرس ایمیل</FormLabel>
              <FormControl>
                <Input type="email" placeholder="example@mail.com" className="rounded-lg bg-background border-input focus:ring-secondary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "لطفاً یک شماره تماس معتبر وارد کنید"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">شماره تماس <span className="text-muted-foreground font-normal">(اختیاری)</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="۰۹۱۲۰۰۰۰۰۰۰" className="rounded-lg bg-background border-input focus:ring-secondary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" rules={{
        required: "موضوع الزامی است"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">موضوع</FormLabel>
              <FormControl>
                <Input placeholder="مثلاً: مشاوره فردی" className="rounded-lg bg-background border-input focus:ring-secondary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "شرح مسئله الزامی است"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default">شرح مسئله</FormLabel>
            <FormControl>
              <Textarea placeholder="لطفاً در مورد درخواست خود یا سوالاتی که دارید توضیح دهید." className="min-h-[150px] rounded-lg bg-background border-input focus:ring-secondary resize-y" {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground">
              ارائه جزئیات بیشتر به ما در ارائه راهنمایی بهتر کمک می‌کند.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive text-sm text-center">{submitError}</p>
        </div>}

      <div className="flex justify-center pt-6">
        <Button type="submit" disabled={isSubmitting} size="lg" className="tracking-widest rounded-lg px-10 bg-secondary hover:bg-secondary/90 text-secondary-foreground font-default">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              در حال ارسال...
            </> : "ارسال پیام"}
        </Button>
      </div>
    </Form>;
}
export default function InquiryForm() {
  return <section data-section-id="222546" id="ارسال-پیام-مستقیم" className="bg-accent py-24 px-4 md:px-8 text-foreground">
      <div className="max-w-[800px] mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-default text-3xl md:text-4xl text-foreground font-semibold">
            ارسال پیام مستقیم
          </h2>
          <p className="mt-4 text-muted-foreground font-default">
            برای دریافت مشاوره یا طرح سوالات خود، از طریق فرم زیر با ما در ارتباط باشید.
          </p>
        </div>

        <div className="bg-card p-8 md:p-12 rounded-lg shadow-lg border border-border">
          <FormProvider formKey="direct_message_form_contact_us" formId="direct_message_form_contact_us" sectionName="ارسال-پیام-مستقیم" defaultValues={{
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            pattern: "phone"
          },
          subject: {
            required: true
          },
          message: {
            required: true
          }
        }} submitText="ارسال پیام" successMessage="پیام شما با موفقیت دریافت شد. تیم ما به زودی با شما تماس خواهد گرفت." errorMessage="خطایی در ارسال پیام رخ داد. لطفاً دوباره تلاش کنید.">
            <InquiryFormInner />
          </FormProvider>
        </div>
      </div>
    </section>;
}